import {WidgetFullEditorHostContext} from "../common/WidgetFullEditorPortal"
import React, {FC, useContext, useEffect, useRef, useState} from "react"
import {cancelEditWidget, MarkdownWidgetData, useAppDispatch} from "@dv5/domain"
import {messagesService} from "@dv5/api"
import {isModPressed} from "@dv5/components"

interface Props {
    canvasId: string
    widgetId: string
    data: MarkdownWidgetData
    text: string
    containerRef: React.RefObject<HTMLDivElement | null>
}

export const MarkdownWidgetInlineEditor: FC<Props> = ({
    canvasId,
    text,
    widgetId,
    data,
    containerRef,
}) => {
    const [editingText, setEditingText] = useState('')
    const fullEditorHost = useContext(WidgetFullEditorHostContext)
    const dispatch = useAppDispatch()
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    const adjustHeight = () => {
        const textarea = textareaRef.current
        if (textarea) {
            textarea.style.height = 'auto'
            textarea.style.height = `${textarea.scrollHeight}px`
        }
    }

    useEffect(() => {
        adjustHeight()
    }, [editingText])

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node
            if (containerRef.current && !containerRef.current.contains(target) && !fullEditorHost?.contains(target)) {
                if (editingText !== text) {
                    saveText(editingText)
                } else {
                    onCancelEdit()
                }
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [text, editingText, fullEditorHost])

    const onCancelEdit = () => {
        dispatch(cancelEditWidget({
            canvasId: canvasId,
            changes: widgetId,
        }))
    }

    const saveText = (newText: string) => {
        messagesService.send('canvas.change-widget-data', {
            canvasId: canvasId,
            widgetId: widgetId,
            patchedPaths: ['text'],
            data: {
                ...data,
                text: newText,
            }
        })
    }

    useEffect(() => {
        setEditingText(text)
    }, [text])

    const changeText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEditingText(e.target.value)
    }

    const keyHandler = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && isModPressed(event)) {
            event.preventDefault()
            event.stopPropagation()
            saveText(editingText)
        } else if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
            onCancelEdit()
        }
    }

    return (
        <textarea
            ref={textareaRef}
            value={editingText}
            onChange={changeText}
            onKeyDown={keyHandler}
            autoFocus
            rows={3}
            style={{
                resize: 'none',
                width: '100%',
                minHeight: '24px',
                backgroundColor: 'transparent',
                border: 'none',
                outline: 'none',
                padding: 0,
                fontFamily: '@dv5MonoFont',
                fontSize: '13px',
                lineHeight: '1.5',
                overflow: 'hidden',
            }}
        />
    )
}
